import { Button, Checkbox, MenuItem, TextField } from "@mui/material";
import Modal from "./Modal";
import {
  apiCreateInvoice,
  apiGetOrder,
  apiSendOrderEmail,
  apiUpdateOrder,
  apiUpdateOrderStatus,
} from "util/network/Orders";
import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { OrderStatusEnum, OrderType } from "types/OrderTypes";
import { useDelayReasons } from "hooks/useDelayReasons";
import {
  apiGetProductions,
  apiUpdateProduction,
} from "util/network/Productions";
import { ProductionStatusEnum } from "types/ProductionType";
import {
  apiGetInquiries,
  apiUpdateInquiry,
} from "util/network/Inquiries";
import { InquiryStatusEnum } from "types/InquiryTypes";
import { isInquiryFinished } from "pages/outsourcing_management/constants/inquiryStatusColors";

enum Resources {
  PRODUCTION = 1,
  INQUIRY = 2,
  INVOICE = 3,
  EMAIL = 4,
  INVOICE_EMAIL = 5,
}

type PropsType = {
  orderId: number;
  status: OrderStatusEnum;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (status: OrderStatusEnum) => void;
};

const OrderStatusModal: React.FC<PropsType> = ({
  orderId,
  status,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();

  const [productionsToUpdate, setProductionsToUpdate] = useState<number[]>([]);
  const [inquiriesToUpdate, setInquiriesToUpdate] = useState<number[]>(
    []
  );
  const [selectedResources, setSelectedResources] = useState<Resources[]>([
    Resources.PRODUCTION,
    Resources.INQUIRY,
    Resources.INVOICE,
    Resources.INVOICE_EMAIL,
  ]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [order, setOrder] = useState<OrderType>();
  const [delayReason, setDelayReason] = useState<string>("");
  // EE-216: the same list as the QC delay and the Production Deadline change.
  const {
    selectableOptions: delayReasonOptions,
    isUnavailable: delayReasonsUnavailable,
    labelFor: labelForDelayReason,
  } = useDelayReasons();
  const [showErrors, setShowErrors] = useState<boolean>(false);

  // EE-216: the delay reason can be set on any order being completed, but it
  // is only mandatory once the order is past its estimated shipment date (the
  // Order Delivery Date, not the Production Deadline). A missing shipment date
  // means we cannot tell, so we do not block on it.
  const isDelayed = order?.shipmentDate
    ? dayjs().isAfter(dayjs(order.shipmentDate), "day")
    : false;
  const showDelayReason = status === OrderStatusEnum.COMPLETE;
  const requiresDelayReason = showDelayReason && isDelayed;
  // An order filed under a since-retired reason keeps it on display, so
  // opening the modal does not silently blank it - it just cannot be picked
  // again once changed.
  const savedReasonRetired =
    !!order?.delayReason &&
    !delayReasonOptions.some((option) => option.code === order.delayReason);

  useEffect(() => {
    if (!isOpen) {
      // Start clean the next time the modal is opened.
      setDelayReason("");
      setShowErrors(false);
      return;
    }
    apiGetOrder(token, orderId)
      .then((res) => {
        setOrder(res);
        setDelayReason(res.delayReason ?? "");
      })
      .catch(() => {
        addToast({
          type: "error",
          message: <>Could not load order #{orderId}.</>,
        });
      });
  }, [token, orderId, isOpen]);

  useEffect(() => {
    if (isOpen) {
      apiGetProductions(token, 1, 100, `orderId=${orderId}`).then((res) => {
        setProductionsToUpdate(
          res.items
            .filter((prod) => prod.status == ProductionStatusEnum.TODO)
            .map((prod) => prod.id)
        );
      });
      apiGetInquiries(
        token,
        1,
        100,
        undefined,
        undefined,
        undefined,
        undefined,
        `orderId=${orderId}`
      ).then((res) => {
        setInquiriesToUpdate(
          res.items
            .filter(
              // Already finished inquiries are left alone - Done as well as
              // Closed. Writing CLOSED over a Done inquiry is refused by the
              // transition table, and it would relabel completed work as
              // abandoned if it were not.
              (req) => !isInquiryFinished(req.state)
            )
            .map((req) => req.id)
        );
      });
    }
  }, [token, isOpen]);

  const confirmUpdate = () => {
    if (!token) {
      return;
    }
    if (requiresDelayReason && !delayReason) {
      setShowErrors(true);
      return;
    }
    setIsLoading(true);
    // The delay reason is persisted first, so a failing status update can
    // never leave a completed order without an explanation for the delay.
    // An optional reason is saved the same way; an empty selection clears
    // whatever was stored.
    const reasonChanged =
      showDelayReason && delayReason !== (order?.delayReason ?? "");
    let reasonSaved = !reasonChanged;
    const persistDelayReason = reasonChanged
      ? apiUpdateOrder(token, orderId, {
          delayReason: delayReason === "" ? null : delayReason,
        }).then(() => {
          reasonSaved = true;
        })
      : Promise.resolve();

    persistDelayReason
      .then(() => apiUpdateOrderStatus(token, orderId, status))
      .then((res) => {
        addToast({
          type: "success",
          message: (
            <>
              Order status was updated to <strong>{status}</strong>.
            </>
          ),
        });
        if (
          status == OrderStatusEnum.CANCELLED ||
          status == OrderStatusEnum.COMPLETE
        ) {
          if (selectedResources.includes(Resources.PRODUCTION)) {
            Promise.all(
              productionsToUpdate.map((productionId) =>
                apiUpdateProduction(token, productionId, {
                  status:
                    status == OrderStatusEnum.CANCELLED
                      ? ProductionStatusEnum.CANCELLED
                      : ProductionStatusEnum.DONE,
                })
              )
            ).then((res) => {
              addToast({
                type: "success",
                message: (
                  <>
                    Productions were updated to <strong>{status}</strong>.
                  </>
                ),
              });
            });
          }
          if (selectedResources.includes(Resources.INQUIRY)) {
            Promise.all(
              inquiriesToUpdate.map((inquiryId) =>
                apiUpdateInquiry(
                  token,
                  inquiryId,
                  {
                    // Whether the order was completed or cancelled,
                    // its outstanding inquiries are finished either way, and
                    // CLOSED is the one terminal state (EE-253). It is also
                    // reachable from anywhere, so this bulk write cannot 409
                    // on the flow's no-skipping rule.
                    state: InquiryStatusEnum.CLOSED,
                  },
                  false
                )
              )
            )
              .then(() => {
                addToast({
                  type: "success",
                  message: (
                    <>
                      Inquiries were <strong>closed</strong>.
                    </>
                  ),
                });
              })
              // apiUpdateInquiry rejects on a non-2xx response (EE-253); it
              // used to resolve with whatever the body parsed to. Without this
              // a failed close reported nothing at all and left an unhandled
              // rejection behind, so the order looked done with its inquiries
              // still open.
              .catch((err) => {
                addToast({
                  type: "error",
                  message: (
                    <>
                      The inquiries on this order could not be closed: {`${err}`}
                    </>
                  ),
                  keep: true,
                });
              });
          }
          if (selectedResources.includes(Resources.INVOICE)) {
            // Sending is a flag on the create call, so the invoice email can
            // only go out together with a newly created invoice.
            const sendInvoice = selectedResources.includes(
              Resources.INVOICE_EMAIL
            );
            apiCreateInvoice(token, orderId, sendInvoice).then((res) => {
              if (res.ok)
              {
                addToast({
                  type: "success",
                  message: sendInvoice ? (
                    <>Invoice was created and sent to the customer.</>
                  ) : (
                    <>Invoice was created.</>
                  ),
                });
              }
              else 
              {
                addToast({
                  type: "error",
                  message: <>Error during Invoice creation.</>,
                });
              }
              
            });
          }
          if (selectedResources.includes(Resources.EMAIL)) {
            apiSendOrderEmail(token, orderId).then((res) => {
              addToast({
                type: "success",
                message: <>Order complete email was sent to the customer.</>,
              });
            });
          }
        }
        onSubmit(status as OrderStatusEnum);
      })
      .catch((err) => {
        addToast({
          type: "error",
          message: reasonSaved ? (
            <>
              Failed to update order status to <strong>{status}</strong>.
            </>
          ) : (
            <>
              Failed to save the delay reason. The order status was not
              changed.
            </>
          ),
        });
      })
      .finally(() => {
        setIsLoading(false);
        // Keep the modal open when the delay reason could not be saved, so
        // the reason is not silently lost and the update can be retried.
        if (reasonSaved) {
          onClose();
        }
      });
  };

  const selectableRow = (
    type: Resources,
    text: string,
    disabled: boolean = false
  ) => {
    return (
      <tr key={type}>
        <td>{text}</td>
        <td>
          <Checkbox
            disabled={disabled}
            checked={!disabled && selectedResources.includes(type)}
            onChange={() => {
              if (selectedResources.includes(type)) {
                setSelectedResources(
                  selectedResources.filter((t) => t !== type)
                );
              } else {
                setSelectedResources([...selectedResources, type]);
              }
            }}
          />
        </td>
      </tr>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      {status === OrderStatusEnum.COMPLETE ? (
        <h1>Complete order #{order?.orderNo ?? orderId}</h1>
      ) : (
        <h1>
          Update order #{orderId} to {status}
        </h1>
      )}
      {showDelayReason && (
        <>
          {requiresDelayReason && (
            <p style={{ margin: "0 0 0.5rem" }}>
              Order Delivery Date has passed. Select a delay reason to continue
            </p>
          )}
          <TextField
            select
            fullWidth
            required={requiresDelayReason}
            label={
              requiresDelayReason
                ? "Reason for delay"
                : "Reason for delay (optional)"
            }
            sx={{ mb: 2 }}
            value={delayReason}
            onChange={(e) => {
              setDelayReason(e.target.value);
              if (showErrors) setShowErrors(false);
            }}
            error={showErrors && requiresDelayReason && !delayReason}
            helperText={
              showErrors && requiresDelayReason && !delayReason
                ? "A delay reason is required"
                : undefined
            }
          >
            <MenuItem value="">Select...</MenuItem>
            {savedReasonRetired && order?.delayReason && (
              <MenuItem value={order.delayReason} disabled>
                {labelForDelayReason(order.delayReason)}
              </MenuItem>
            )}
            {delayReasonOptions.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {requiresDelayReason && delayReasonsUnavailable && (
            <p style={{ margin: "0 0 1rem", color: "#d32f2f" }}>
              The delay reasons could not be loaded, so the order cannot be
              completed. Reload the page, and tell an administrator if it
              keeps happening.
            </p>
          )}
        </>
      )}
      {status == OrderStatusEnum.CANCELLED ||
      status == OrderStatusEnum.COMPLETE ? (
        <>
          <h2>
            {status === OrderStatusEnum.COMPLETE
              ? "Actions on completion"
              : "The following resources will be affected from this update:"}
          </h2>
          <table>
            <tbody>
              {productionsToUpdate.length > 0 &&
                selectableRow(
                  Resources.PRODUCTION,
                  `Productions (${productionsToUpdate.length}) will be set to ${
                    status == OrderStatusEnum.CANCELLED
                      ? "cancelled"
                      : "complete"
                  }`
                )}
              {inquiriesToUpdate.length > 0 &&
                selectableRow(
                  Resources.INQUIRY,
                  `Inquiries (${
                    inquiriesToUpdate.length
                  }) will be set to ${
                    status == OrderStatusEnum.CANCELLED
                      ? "cancelled"
                      : "received"
                  }`
                )}
              {status == OrderStatusEnum.COMPLETE &&
                selectableRow(Resources.INVOICE, `Create invoice`)}
              {status === OrderStatusEnum.COMPLETE &&
                selectableRow(
                  Resources.INVOICE_EMAIL,
                  `Send invoice to customer`,
                  !selectedResources.includes(Resources.INVOICE)
                )}
              {status == OrderStatusEnum.COMPLETE &&
                selectableRow(Resources.EMAIL, `Send order completion email`)}
            </tbody>
          </table>
        </>
      ) : null}
      <div
        style={{
          display: "flex",
          // Right-aligned with an 8px gap, as MUI DialogActions lays out the
          // requisition modal's buttons.
          justifyContent: "flex-end",
          gap: "8px",
          width: "100%",
          marginTop: "1.5rem",
        }}
      >
        <Button
          variant="contained"
          color="inherit"
          onClick={() => onClose()}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={confirmUpdate}
          disabled={
            isLoading || (requiresDelayReason && delayReasonsUnavailable)
          }
        >
          {status === OrderStatusEnum.COMPLETE ? "Complete order" : "Confirm"}
        </Button>
      </div>
    </Modal>
  );
};

export default OrderStatusModal;
